import { message } from "antd";
import { useNavigate } from "react-router-dom";
import type { MembershipProduct } from "../SpaceType";
import { membershipCanEnterRetailPurchase, membershipRetailPurchaseState } from "../membershipPurchaseUtils";
import { h5NavigateTo } from "../../utils/miniProgramEnv";
import { guardDouyinMiniProgramSubscribe } from "../../utils/douyinMiniProgramSubscribeGuard";
import { membershipNoRetailPurchaseHint } from "../spacePageUtils";

function primaryButtonLabel(row: MembershipProduct, upgradeEligible: boolean, canBuyRetail: boolean): string {
  if (upgradeEligible) {
    return "升级";
  }
  if (canBuyRetail) {
    return "订阅";
  }
  return "订阅";
}

function renderSubscribedMeta(
  row: MembershipProduct,
  isMarket: boolean
): JSX.Element | null {
  if (!row.userSubscribed) {
    return null;
  }
  if (isMarket) {
    const n =
      row.subscribedStrategyCount ??
      row.strategyEntitlements?.filter((e) => e.active).length ??
      0;
    return (
      <div className="space-home-m-title-meta">
        <span className="space-home-m-days">已订阅{n}个策略</span>
        <span className="space-home-m-tag space-home-m-tag--active">已开通</span>
      </div>
    );
  }
  const d = row.daysRemaining;
  return (
    <div className="space-home-m-title-meta">
      {d != null && d >= 0 ? <span className="space-home-m-days">剩余{d}天</span> : null}
      <span className="space-home-m-tag space-home-m-tag--active">已开通</span>
    </div>
  );
}

/** 权益订阅卡片（对齐 web 产品卡：打包黑底流光，无图标） */
export function SpaceHomeMembershipCard(props: {
  row: MembershipProduct;
  spaceId: string;
  isLoggedIn: boolean;
  onLoginRequest: () => void;
}) {
  const { row, spaceId, isLoggedIn, onLoginRequest } = props;
  const navigate = useNavigate();
  const isBundle = row.productKind === "product_bundle";
  const { isMarket, canBuyMore, canBuyRetail } = membershipRetailPurchaseState(row);
  const upgradeEligible =
    row.upgradeEligible === true && !!row.upgradeSourceSpaceProductId?.trim();

  const onPurchaseClick = (mode: "purchase" | "upgrade" | "renew" | "more") => {
    if (!isLoggedIn) {
      onLoginRequest();
      return;
    }
    if (mode === "upgrade") {
      message.info("升级功能移动端接入中");
      return;
    }
    if (!membershipCanEnterRetailPurchase(row)) {
      message.info(membershipNoRetailPurchaseHint(row));
      return;
    }
    if (row.userSubscribed && !canBuyMore) {
      message.info("暂无可续订规格");
      return;
    }
    guardDouyinMiniProgramSubscribe(() => {
      h5NavigateTo(navigate, `/space/${spaceId}/subscribe/${encodeURIComponent(row.spaceProductId)}`);
    });
  };

  const renderFooterAction = (): JSX.Element | null => {
    if (row.userSubscribed) {
      if (!canBuyMore) {
        return null;
      }
      return (
        <button
          type="button"
          className="space-home-m-btn space-home-m-btn--pill space-home-m-btn--renew"
          onClick={() => onPurchaseClick(isMarket ? "more" : "renew")}
        >
          {isMarket ? "订阅更多" : "续订"}
        </button>
      );
    }

    if (upgradeEligible) {
      return (
        <button
          type="button"
          className="space-home-m-btn space-home-m-btn--pill space-home-m-btn--subscribe space-home-m-btn--upgrade"
          onClick={() => onPurchaseClick("upgrade")}
        >
          升级
        </button>
      );
    }

    if (membershipCanEnterRetailPurchase(row)) {
      return (
        <button
          type="button"
          className="space-home-m-btn space-home-m-btn--pill space-home-m-btn--subscribe"
          onClick={() => onPurchaseClick("purchase")}
        >
          {primaryButtonLabel(row, upgradeEligible, canBuyRetail)}
        </button>
      );
    }

    return (
      <span className="space-home-m-no-spec-hint">{membershipNoRetailPurchaseHint(row)}</span>
    );
  };

  const footerAction = renderFooterAction();

  return (
    <li
      className={
        isBundle ? "space-home-m-item space-home-m-item--bundle" : "space-home-m-item"
      }
    >
      <div className="space-home-m-body">
        <div className="space-home-m-main">
          <div className="space-home-m-title-row">
            <div className="space-home-m-name">{row.displayName}</div>
            {renderSubscribedMeta(row, isMarket)}
          </div>
          <div className="space-home-m-content-row">
            {row.description?.trim() ? (
              <p className="space-home-m-intro">{row.description.trim()}</p>
            ) : null}
            {footerAction ? (
              <div className="space-home-m-footer-action">{footerAction}</div>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}
